import { useMemo } from 'react'

import cn from 'classnames'
import { monthOptions as externalMonthOptions } from 'mmfintech-commons'

import { Input, InputError, InputLabel } from 'mmfintech-portal-commons'

import './inputDateFields.scss'

const MAX_AGE = 120
const MIN_AGE = 16

interface InputDateFieldsProps {
  label?: string
  value: Date
  setValue: (value: Date) => void
  required?: boolean
  error?: string
}

const InputDateFields = (props: InputDateFieldsProps) => {
  const { label, value, setValue, required, error } = props

  const dayOptions = useMemo(() => {
    const date = value ?? new Date()
    const days = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
    const res = []
    for (let i = 1; i <= days; i++) {
      res.push({ value: String(i), label: `${i < 10 ? '0' : ''}${i}` })
    }
    return res
  }, [value])

  const monthOptions = useMemo(() => externalMonthOptions(), [])

  const yearOptions = useMemo(() => {
    const date = new Date()
    const min = date.getFullYear() - MAX_AGE
    const res = []
    for (let i = date.getFullYear() - MIN_AGE; i > min; i--) {
      res.push({ value: String(i), label: String(i) })
    }
    return res
  }, [])

  const handleDayChange = (day: string) => {
    const date = value ?? new Date()
    setValue(new Date(date.getFullYear(), date.getMonth(), Number(day)))
  }

  const handleMonthChange = (month: string) => {
    const date = value ?? new Date()
    const newMonth = Number(month) - 1
    const maxDay = new Date(date.getFullYear(), newMonth + 1, 0).getDate()
    const newDay = Math.min(date.getDate(), maxDay)
    setValue(new Date(date.getFullYear(), newMonth, newDay))
  }

  const handleYearChange = (year: string) => {
    const date = value ?? new Date()
    const newYear = Number(year)
    const maxDay = new Date(newYear, date.getMonth() + 1, 0).getDate()
    const newDay = Math.min(date.getDate(), maxDay)
    setValue(new Date(newYear, date.getMonth(), newDay))
  }

  return (
    <>
      <InputLabel label={label} required={required} requiredPosition='right' className={cn({ required })} />
      <div className='input-date-fields-wrapper'>
        <Input
          type='select'
          name='day'
          options={dayOptions}
          value={value ? String(value.getDate()) : null}
          onChange={(_name: string, value: string) => handleDayChange(value)}
          isSearchable={false}
          placeholder='DD'
          hideErrorLine
        />
        <Input
          type='select'
          name='month'
          options={monthOptions}
          value={value ? String(value.getMonth() + 1) : null}
          onChange={(_name: string, value: string) => handleMonthChange(value)}
          isSearchable={false}
          placeholder='MM'
          hideErrorLine
        />
        <Input
          type='select'
          name='year'
          options={yearOptions}
          value={value ? String(value.getFullYear()) : null}
          onChange={(_name: string, value: string) => handleYearChange(value)}
          isSearchable={false}
          placeholder='YYYY'
          hideErrorLine
        />
      </div>
      <InputError error={error} hideErrorLine={false} hasIcon={false} />
    </>
  )
}

export default InputDateFields
